import React, { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';

import { rovoAgentsPluginKey } from '../../../../pm-plugins/rovo-agents/plugin-key';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useCommandPaletteStateMachine } from '../../../hooks/useCommandPaletteStateMachine';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useOutsideClickHandler } from '../../../hooks/useOutsideClickHandler';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import type { AIExperienceMachineContext } from '../../../utils/xstate/get-ai-experience-service';

import { RovoPromptScreenWithLogic } from './RovoPromptScreenWithLogic';

const usePromptLogic = (
	ctx: AIExperienceMachineContext,
	ai: ReturnType<typeof useAIExperienceCommonDataContext>,
	userADFInputOverride?: JSONDocNode,
) => {
	const {
		context: { textInput, adfInput },
		send,
	} = useCommandPaletteStateMachine({
		baseGenerate: ctx.baseGenerate,
		startingUserInput: ctx.userInput,
		startingUserADFInput: userADFInputOverride ?? ctx.userADFInput,
		startingConfig: ctx.configItem,
	});
	const adfRef = useRef<JSONDocNode | undefined>(undefined);
	const textInputRef = useRef<string | undefined>(undefined);

	const onTextChange = useCallback(
		(input: string) => {
			send({ type: 'onTextEntered', input });
			textInputRef.current = input;
		},
		[send],
	);

	const onADFChange = useCallback(
		(adf?: JSONDocNode) => {
			send({ type: 'onADFEntered', adf });
			adfRef.current = adf;
		},
		[send],
	);

	const submit = useCallback(() => {
		const adfInput = adfRef.current;
		const textInput = textInputRef.current;
		if (!textInput?.trim()) {
			return;
		}
		ai.sendToAIExperienceMachine({
			type: 'kickoff with command palette input',
			context: {
				configItem: ctx.configItem,
				userInput: textInput,
				userADFInput: adfInput,
			},
		});
	}, [ctx.configItem, ai]);

	const cancel = useCallback(
		(options?: { shouldClearSelectionContext?: boolean }) => {
			ai.endExperience({ shouldClearSelectionContext: options?.shouldClearSelectionContext });
		},
		[ai],
	);

	const discard = useCallback(() => {
		ai.sendToAIExperienceMachine({
			type: 'update context',
			context: {
				configItem: ctx.configItem,
				userInput: textInput || '',
				userADFInput: adfInput,
			},
		});
		ai.sendToAIExperienceMachine({ type: 'enable discard' });
	}, [adfInput, ai, ctx.configItem, textInput]);

	return { textInput, adfInput, onTextChange, onADFChange, submit, cancel, discard };
};

const usePromptAnalytics = (ctx: AIExperienceMachineContext) => {
	const intl = useIntl();
	useAnalyticsFlowEventsQueue({
		stepName: 'prompt',
		callerComponent: 'PromptScreenWithLogic',
		attributes: {
			experienceName: ctx.configItem.key,
			featureId: ctx.configItem.featureId,
			promptTitle: ctx.configItem.promptLabel
				? intl.formatMessage(ctx.configItem.promptLabel)
				: undefined,
			promptType: 'generate',
			usingCommandPalette: true,
			retryPromptCount: ctx.retryPromptCount,
			editCount: ctx.editCount,
		},
	});
};

type Props = { context: AIExperienceMachineContext; userADFInput?: JSONDocNode };

export const PromptScreenWithLogic = ({
	context,
	userADFInput: userADFInputProp,
}: Props): React.JSX.Element | null => {
	const ai = useAIExperienceCommonDataContext();
	const { isInDocumentStreamingEnabled } = useContentComponentContext();
	const { textInput, adfInput, onTextChange, onADFChange, submit, cancel } = usePromptLogic(
		context,
		ai,
		userADFInputProp,
	);
	usePromptAnalytics(context);
	useOutsideClickHandler({
		modalRef: ai.modalRef,
		onCancel: () => {
			// We let the RovoPromptScreenWithLogic handle aborts
			if (!ai.editorPluginAIProvider.onSubmit) {
				cancel();
			}
		},
		onDiscard: () => {},
	});

	// Callbacks for the openInRovoChat flow (SmartLink Rovo button). These must be
	// defined at the top level (before any early return) to satisfy React hooks rules.
	const rovoChatAIFeatureContext = context.configItem.rovoChatAIFeatureContext;
	const rovoChatInteractionSource = context.configItem.rovoChatInteractionSource;
	const { editorView } = ai;
	const editorProduct = ai.editorPluginAIProvider.product;

	const publishChatNew = useCallback(
		(prompt: DocNode) => {
			const publish = rovoAgentsPluginKey.getState(editorView.state)?.publish;
			if (publish) {
				const payload: Parameters<typeof publish>[0] = {
					type: 'chat-new',
					product: editorProduct,
					data: {
						dialogues: [],
						prompt,
						...(rovoChatAIFeatureContext ? { aiFeatureContext: rovoChatAIFeatureContext } : {}),
					},
					source: 'editor',
					...(rovoChatInteractionSource ? { interactionSource: rovoChatInteractionSource } : {}),
				};
				publish(payload);
			}
		},
		[editorView, editorProduct, rovoChatAIFeatureContext, rovoChatInteractionSource],
	);

	const onBeforeSubmitForRovoChat = useCallback(
		(prompt: DocNode) => {
			publishChatNew(prompt);
			cancel();
			return true;
		},
		[publishChatNew, cancel],
	);

	const openInRovoChatConfig = useMemo(
		() => ({ onBeforeSubmit: onBeforeSubmitForRovoChat }),
		[onBeforeSubmitForRovoChat],
	);

	// When openInRovoChat is true, intercept the submit to open the Rovo chat
	// sidebar instead of performing an AI edit. The SmartLink Rovo button only
	// appears in Rovo-enabled products (onSubmit is always set).
	if (context.configItem.openInRovoChat) {
		if (ai.editorPluginAIProvider.onSubmit) {
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				return null;
			}
			return (
				<RovoPromptScreenWithLogic cancel={cancel} openInRovoChatConfig={openInRovoChatConfig} />
			);
		}

		return null;
	}

	if (ai.editorPluginAIProvider.onSubmit) {
		if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
			return null;
		}
		return <RovoPromptScreenWithLogic cancel={cancel} />;
	} else {
		return (
			<PromptScreen
				textInput={textInput}
				adfInput={adfInput}
				onTextChange={onTextChange}
				onADFChange={onADFChange}
				submit={submit}
				cancel={cancel}
				PromptEditor={ai.editorPluginAIProvider.PromptEditor}
				appearance={ai.appearance}
				isAIFC={isInDocumentStreamingEnabled}
			/>
		);
	}
};
